// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://{server}/o/{tenant}/mcp` | (string & {});
};

/**
 * Error payload returned by the controller.
 */
export type Error = {
    /**
     * Human-readable description of the error.
     */
    message?: string;
};

/**
 * Parameters for registering a new MCP client.
 */
export type McpClientCreateRequest = ({
    type: 'client_credential_post';
} & McpConfidentialClientCreateRequest) | ({
    type: 'jwt_bearer';
} & McpFederatedClientCreateRequest);

/**
 * Properties common to every client registration.
 */
export type McpClientCreateBase = {
    /**
     * The authentication method for the client.
     */
    type: string;
};

/**
 * Parameters to register a confidential client. This client authenticates
 * with a P0-issued secret.
 *
 */
export type McpConfidentialClientCreateRequest = McpClientCreateBase & {
    type?: 'client_credential_post';
    /**
     * Hostname of the machine the client runs on.
     */
    hostname: string;
    /**
     * Client platform.
     */
    platform: 'claude-code' | 'claude-agents' | 'custom';
    /**
     * OAuth redirect URI for the client.
     */
    redirectUri: string;
    /**
     * Client version.
     */
    version: string;
};

/**
 * Parameters to register an agent identity. This client authenticates with a
 * JWT from an installed identity provider. P0 does not create a secret.
 *
 * Use this request when the identity provider does not permit dynamic
 * registration.
 *
 */
export type McpFederatedClientCreateRequest = McpClientCreateBase & {
    type?: 'jwt_bearer';
    /**
     * The ID of an installed `identity-provider` component.
     */
    providerId: string;
    /**
     * The identity in the agent's token. Use the `external_sub` claim if
     * the identity provider sets one. If not, use the verified email, and
     * then the `sub` claim.
     *
     */
    identity: string;
};

/**
 * Parameters for validating an MCP client secret.
 */
export type McpClientValidateRequest = {
    /**
     * SHA-256 hash (base64) of the client secret to validate.
     */
    secretHash: string;
};

/**
 * Customer-managed fields that may be updated on an MCP client.
 */
export type McpClientUpdates = {
    /**
     * Human-friendly client name, or null to clear it.
     */
    displayName?: string | null;
    /**
     * Email of the P0 user who owns the client, or null to clear it.
     * Defaults to the registering user when a user registers the client;
     * absent for a client a gateway registered dynamically. Unlike
     * `createdBy`, the owner may change over time.
     *
     */
    owner?: string | null;
    /**
     * Whether the client may authenticate and refresh.
     */
    status?: 'active' | 'disabled';
};

/**
 * At least one customer-managed field to update on an MCP client.
 */
export type McpClientUpdateRequest = McpClientUpdates;

/**
 * Parameters for validating a client's JWT identity.
 */
export type McpFederationValidateRequest = {
    /**
     * The JWT payload
     */
    claims: {
        /**
         * OAuth issuer
         */
        iss: string;
        /**
         * OAuth subject
         */
        sub: string;
        /**
         * Provider-set identity
         */
        external_sub?: string;
    };
};

/**
 * Identity of a successful validation
 */
export type McpFederationValidatedIdentity = {
    /**
     * The identity subject that should be issued
     */
    sub: string;
};

/**
 * Properties common to every MCP client.
 */
export type McpClientCommon = {
    /**
     * Creation time, in epoch milliseconds.
     */
    createdAt?: number;
    /**
     * Unique identifier of the client. For a federated client, this is the
     * subject that P0 issues.
     *
     */
    id: string;
};

/**
 * A client that authenticates with a P0-issued secret. This client
 * represents one installation of an agentic tool.
 *
 */
export type McpConfidentialClient = McpClientCommon & {
    type: 'client_credential_post';
    /**
     * Who created the client.
     */
    createdBy: {
        /**
         * Email of the P0 user that created the client.
         */
        user: string;
        /**
         * IP address the create request originated from, when known.
         */
        ip?: string;
    };
    /**
     * Hostname of the machine the client runs on.
     */
    hostname: string;
    /**
     * Client platform.
     */
    platform: string;
    /**
     * OAuth redirect URI for the client.
     */
    redirectUri: string;
    /**
     * Client version.
     */
    version: string;
};

/**
 * An agent identity that authenticates by presenting a JWT from an
 * installed identity provider. Created either by manual enrolment or, when
 * the provider allows dynamic registration, by a gateway on first use.
 *
 */
export type McpFederatedClient = McpClientCommon & {
    type: 'jwt_bearer';
    /**
     * Who enrolled the identity: a P0 user for a manual registration, or
     * the gateway that first validated it under dynamic registration.
     *
     */
    createdBy: {
        /**
         * Email of the P0 user that enrolled the identity.
         */
        user: string;
        /**
         * IP address the create request originated from, when known.
         */
        ip?: string;
    } | {
        /**
         * ID of the gateway that registered the identity.
         */
        gateway: string;
    };
    /**
     * Issuer (`iss`) of this client's JWTs, from its provider.
     */
    issuer: string;
    /**
     * The ID of the `identity-provider` component for this identity.
     */
    providerId: string;
};

export type McpConfidentialClientWithSecret = McpConfidentialClient & {
    /**
     * The client secret, returned only at creation time.
     */
    secret: string;
};

/**
 * An MCP client as returned in listings. Secrets are never included.
 */
export type McpClientListItem = McpClientUpdates & (({
    type: 'client_credential_post';
} & McpConfidentialClient) | ({
    type: 'jwt_bearer';
} & McpFederatedClient));

/**
 * An MCP server available to the tenant.
 */
export type McpServer = ({
    type: 'container';
} & McpContainerServer) | ({
    type: 'internal';
} & McpInternalServer) | ({
    type: 'upstream';
} & McpUpstreamServer);

export type McpServerCommon = {
    /**
     * Unique identifier of the server.
     */
    id: string;
    /**
     * URL at which the server is reached.
     */
    url: string;
    /**
     * Optional logo URL for display.
     */
    logoUrl?: string;
};

/**
 * A server hosted as a container within a gateway.
 */
export type McpContainerServer = McpServerCommon & {
    type: 'container';
};

/**
 * A server provided internally by P0 (e.g. the built-in P0 server).
 */
export type McpInternalServer = McpServerCommon & {
    type: 'internal';
};

/**
 * A server that proxies an upstream MCP endpoint.
 */
export type McpUpstreamServer = McpServerCommon & {
    type: 'upstream';
    /**
     * URL of the upstream MCP server being proxied.
     */
    upstreamUrl: string;
};

/**
 * Agent protocol an activity entry was logged for. `protocol` is
 * always present on entries returned by this endpoint: the server
 * normalizes stored records that predate this field to `mcp` before
 * returning them.
 *
 */
export const AgentActivityProtocol = { MCP: 'mcp', A2A: 'a2a' } as const;

/**
 * Agent protocol an activity entry was logged for. `protocol` is
 * always present on entries returned by this endpoint: the server
 * normalizes stored records that predate this field to `mcp` before
 * returning them.
 *
 */
export type AgentActivityProtocol = typeof AgentActivityProtocol[keyof typeof AgentActivityProtocol];

/**
 * The risk filter's own value, lowercase (distinct from
 * resolvedRisk.level's capitalized display form on each entry in the
 * response body). `unknown` means the entry's risk was evaluated but
 * could not be resolved against the IAM risk catalog — distinct from
 * an entry with no resolved risk at all.
 *
 */
export const AgentActivityRiskLevelParam = {
    CRITICAL: 'critical',
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
    UNKNOWN: 'unknown'
} as const;

/**
 * The risk filter's own value, lowercase (distinct from
 * resolvedRisk.level's capitalized display form on each entry in the
 * response body). `unknown` means the entry's risk was evaluated but
 * could not be resolved against the IAM risk catalog — distinct from
 * an entry with no resolved risk at all.
 *
 */
export type AgentActivityRiskLevelParam = typeof AgentActivityRiskLevelParam[keyof typeof AgentActivityRiskLevelParam];

/**
 * A gateway whose activity could not be read for this response, and
 * why.
 *
 */
export type AgentActivityUnavailableGateway = {
    /**
     * The installed gateway's component id.
     */
    gatewayId: string;
    /**
     * Why the gateway's activity is missing. Stable, so clients may
     * branch on it.
     * - `unsupported`: the gateway cannot serve log queries, because
     * it has no log sink or its build predates the query route.
     * - `timeout`: the gateway did not answer in time.
     * - `unreachable`: the gateway, its tunnel, or its log sink could
     * not be reached.
     * - `failed`: any other error, such as rejected credentials.
     *
     */
    reason: 'unsupported' | 'timeout' | 'unreachable' | 'failed';
};

/**
 * OpenTelemetry trace correlation for an activity entry. Present only
 * when trace propagation was enabled for the request that produced the
 * entry; absent on records logged before trace propagation existed.
 * Not queryable by this endpoint.
 *
 */
export type AgentActivityTraceCorrelation = {
    /**
     * Whether the trace was sampled for export.
     */
    sampled: boolean;
    /**
     * W3C span ID of the span that logged this entry.
     */
    spanId: string;
    /**
     * W3C trace ID of the OpenTelemetry trace that produced this entry.
     */
    traceId: string;
};

/**
 * One party in an RFC 8693 delegation chain: the identity of an agent or
 * client that acted, as asserted by the token it presented.
 *
 */
export type AgentActivityActor = {
    /**
     * Issuer that authenticated this actor.
     */
    iss: string;
    /**
     * Subject identifying this actor at its issuer.
     */
    sub: string;
};

/**
 * A single agent activity entry: a product projection of the
 * structured OpenTelemetry log record emitted for one MCP tool call or
 * one A2A agent invocation. Discriminated on `protocol`.
 *
 */
export type AgentActivityEntry = ({
    protocol: 'mcp';
} & McpAgentActivityEntry) | ({
    protocol: 'a2a';
} & A2aAgentActivityEntry);

/**
 * Fields common to every agent activity entry, regardless of protocol.
 */
export type AgentActivityEntryCommon = {
    /**
     * RFC 8693 delegation chain carried by the caller's token, ordered
     * from the acting party to the originating party. `caller` is the
     * party the chain acted for, and is not repeated here. Absent when
     * the call was not delegated. Not queryable by this endpoint.
     *
     */
    actors?: Array<AgentActivityActor>;
    /**
     * Address the call was made to, as the token's audience records it:
     * the gateway URL of the MCP server or A2A bridge it reached.
     *
     */
    address?: string;
    /**
     * Whether the gateway's entitlement check allowed the call.
     * `false` means the call was denied. Absent on calls that were not
     * subject to an entitlement check.
     *
     */
    allowed?: boolean;
    /**
     * Caller email, or JWT subject when email is absent.
     */
    caller?: string;
    /**
     * OAuth authorized party identifying the registered client.
     */
    clientId?: string;
    /**
     * Direction of the message relative to the gateway.
     */
    direction?: 'inbound' | 'outbound';
    /**
     * Identifier the underlying event store assigned to the log record, when one was assigned.
     */
    id?: string;
    /**
     * Message kind, such as `request` or `response`.
     */
    messageKind?: string;
    /**
     * Method carried by the logged message, when known.
     */
    method?: string;
    protocol: AgentActivityProtocol;
    /**
     * Session the call belongs to, when known.
     */
    sessionId?: string;
    /**
     * When the call occurred.
     */
    timestamp: string;
    trace?: AgentActivityTraceCorrelation;
};

/**
 * An MCP tool-call activity entry.
 */
export type McpAgentActivityEntry = AgentActivityEntryCommon & {
    protocol: 'mcp';
    /**
     * Structured arguments supplied to the MCP tool call, when known.
     */
    arguments?: {
        [key: string]: unknown;
    };
    /**
     * MCP server the call was made against, when known.
     */
    mcpServerName?: string;
    /**
     * Access request ID extracted from tool arguments, when present.
     */
    requestId?: string;
    /**
     * Name of the tool that was called, when known.
     */
    toolName?: string;
};

/**
 * An A2A agent-invocation activity entry. Does not carry `arguments`:
 * A2A request parameters are not part of this contract.
 *
 */
export type A2aAgentActivityEntry = AgentActivityEntryCommon & {
    protocol: 'a2a';
    /**
     * A2A agent identifier the call was made against.
     */
    agentId?: string;
    /**
     * Upstream base URL the A2A bridge forwards to, when known.
     */
    bridgeBaseUrl?: string;
    /**
     * Stable identifier of the A2A bridge the call was made against, when
     * known. Tells apart bridges that share an upstream.
     *
     */
    bridgeId?: string;
    /**
     * Gateway path the call was made on, when known.
     */
    bridgePath?: string;
};

export type ListMcpClientsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/clients';
};

export type ListMcpClientsErrors = {
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
};

export type ListMcpClientsError = ListMcpClientsErrors[keyof ListMcpClientsErrors];

export type ListMcpClientsResponses = {
    /**
     * The tenant's registered clients.
     */
    200: {
        clients: Array<McpClientListItem>;
    };
};

export type ListMcpClientsResponse = ListMcpClientsResponses[keyof ListMcpClientsResponses];

export type CreateMcpClientData = {
    body: McpClientCreateRequest;
    path?: never;
    query?: never;
    url: '/clients';
};

export type CreateMcpClientErrors = {
    /**
     * The request was malformed or failed validation.
     */
    400: Error;
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
    /**
     * The resource already exists.
     */
    409: Error;
    /**
     * The request refers to a resource that is not available. The `providerId`
     * property must name an installed identity provider.
     *
     */
    422: Error;
};

export type CreateMcpClientError = CreateMcpClientErrors[keyof CreateMcpClientErrors];

export type CreateMcpClientResponses = {
    /**
     * The registered client: a confidential client with its one-time
     * secret, or a federated client, which has none.
     *
     */
    200: {
        client: McpConfidentialClientWithSecret | McpFederatedClient;
    };
};

export type CreateMcpClientResponse = CreateMcpClientResponses[keyof CreateMcpClientResponses];

export type ValidateMcpClientData = {
    body: McpClientValidateRequest;
    path: {
        /**
         * Identifier of the MCP client to validate.
         */
        clientId: string;
    };
    query?: never;
    url: '/clients/{clientId}/validate';
};

export type ValidateMcpClientErrors = {
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
};

export type ValidateMcpClientError = ValidateMcpClientErrors[keyof ValidateMcpClientErrors];

export type ValidateMcpClientResponses = {
    /**
     * The validation result.
     */
    200: {
        /**
         * True when the presented secret hash matches the stored hash.
         */
        ok: boolean;
        /**
         * The matched client, or absent when no such client exists.
         */
        client?: McpConfidentialClient;
    };
};

export type ValidateMcpClientResponse = ValidateMcpClientResponses[keyof ValidateMcpClientResponses];

export type UpdateMcpClientData = {
    body: McpClientUpdateRequest;
    path: {
        /**
         * Identifier of the MCP client to update.
         */
        clientId: string;
    };
    query?: never;
    url: '/clients/{clientId}';
};

export type UpdateMcpClientErrors = {
    /**
     * The request was malformed or failed validation.
     */
    400: Error;
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
    /**
     * The requested resource does not exist.
     */
    404: Error;
};

export type UpdateMcpClientError = UpdateMcpClientErrors[keyof UpdateMcpClientErrors];

export type UpdateMcpClientResponses = {
    /**
     * The updated client.
     */
    200: {
        client: McpClientListItem;
    };
};

export type UpdateMcpClientResponse = UpdateMcpClientResponses[keyof UpdateMcpClientResponses];

export type ValidateFederationIdentityData = {
    body: McpFederationValidateRequest;
    path?: never;
    query?: never;
    url: '/federation/validate';
};

export type ValidateFederationIdentityErrors = {
    /**
     * The request was malformed or failed validation.
     */
    400: Error;
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
};

export type ValidateFederationIdentityError = ValidateFederationIdentityErrors[keyof ValidateFederationIdentityErrors];

export type ValidateFederationIdentityResponses = {
    /**
     * The validation result.
     */
    200: {
        /**
         * True when the presented claims are valid. False otherwise.
         */
        ok: boolean;
        identity?: McpFederationValidatedIdentity;
        /**
         * Present only when ok is true; the matched identity provider's configured replay-protection policy, for the caller to enforce locally.
         */
        replayProtection?: {
            mode: 'disabled' | 'strict' | 'grace';
            /**
             * Present only when mode is grace.
             */
            graceSeconds?: number;
        };
    };
};

export type ValidateFederationIdentityResponse = ValidateFederationIdentityResponses[keyof ValidateFederationIdentityResponses];

export type ListMcpServersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/servers';
};

export type ListMcpServersErrors = {
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
};

export type ListMcpServersError = ListMcpServersErrors[keyof ListMcpServersErrors];

export type ListMcpServersResponses = {
    /**
     * The available MCP servers.
     */
    200: {
        servers: Array<McpServer>;
    };
};

export type ListMcpServersResponse = ListMcpServersResponses[keyof ListMcpServersResponses];

export type GetMcpServerData = {
    body?: never;
    path: {
        /**
         * Identifier of the MCP server to retrieve.
         */
        serverId: string;
    };
    query?: never;
    url: '/servers/{serverId}';
};

export type GetMcpServerErrors = {
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
    /**
     * The requested resource does not exist.
     */
    404: Error;
};

export type GetMcpServerError = GetMcpServerErrors[keyof GetMcpServerErrors];

export type GetMcpServerResponses = {
    /**
     * The requested MCP server.
     */
    200: {
        server: McpServer;
    };
};

export type GetMcpServerResponse = GetMcpServerResponses[keyof GetMcpServerResponses];

export type ListAgentActivityData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Opaque pagination cursor returned as `nextPageToken` from a prior page.
         */
        pageToken?: string;
        /**
         * Maximum number of entries to return per page.
         */
        pageSize?: string;
        /**
         * Restrict results to this caller email or JWT subject.
         */
        caller?: string;
        /**
         * Restrict results to this OAuth authorized party.
         */
        clientId?: string;
        /**
         * Restrict results to MCP calls of this tool.
         */
        toolName?: string;
        /**
         * Restrict results to this session.
         */
        sessionId?: string;
        /**
         * Restrict results to calls against this MCP server or A2A bridge.
         */
        mcpServerName?: string;
        /**
         * Restrict results to entries logged for this agent protocol.
         */
        protocol?: AgentActivityProtocol;
        /**
         * Restrict results to entries whose resolved risk is this level.
         */
        risk?: AgentActivityRiskLevelParam;
        /**
         * Restrict results to A2A calls addressed to this agent.
         */
        agentId?: string;
        /**
         * Inclusive lower bound on entry timestamp. Must not be after
         * `endTime`, and must not be earlier than the same instant one
         * calendar year before `endTime`.
         * A range outside these bounds is rejected with 400.
         *
         */
        startTime?: string;
        /**
         * Inclusive upper bound on entry timestamp.
         */
        endTime?: string;
    };
    url: '/activity';
};

export type ListAgentActivityErrors = {
    /**
     * The request was malformed or failed validation.
     */
    400: Error;
    /**
     * Authentication is missing or invalid.
     */
    401: Error;
    /**
     * The caller lacks the permission required for this operation.
     */
    403: Error;
};

export type ListAgentActivityError = ListAgentActivityErrors[keyof ListAgentActivityErrors];

export type ListAgentActivityResponses = {
    /**
     * Matching agent activity entries.
     */
    200: {
        entries: Array<AgentActivityEntry>;
        /**
         * Whether any installed gateway links a log provider. When
         * false, `entries` is always empty because there is nowhere
         * to read activity from — distinguish this from an empty
         * result where no activity occurred or filters matched nothing.
         *
         */
        logProviderConfigured: boolean;
        /**
         * Cursor for the next page, absent on the last page.
         */
        nextPageToken?: string;
        /**
         * Gateways whose activity `entries` omits because they
         * could not be read. The rest of the response is still
         * valid. Absent for log providers that are not queried
         * through each gateway.
         *
         */
        unavailableGateways?: Array<AgentActivityUnavailableGateway>;
    };
};

export type ListAgentActivityResponse = ListAgentActivityResponses[keyof ListAgentActivityResponses];
